import { useCallback, useMemo, useState } from "react";

/**
 * Query hook signature that the factory expects
 */
export type EntityQueryHook<TEntity, TId> = (id: TId | null) => {
  data?: TEntity;
  isLoading: boolean;
};

/**
 * Configuration for the entity modal hook factory
 */
export interface EntityModalHookConfig<TEntity, TId> {
  /** The query hook that fetches the entity by ID */
  useQueryHook: EntityQueryHook<TEntity, TId>;
  /** Function to extract the entity ID for comparison (prevents stale cache issues) */
  getEntityId: (entity: TEntity) => TId;
  /** Whether this modal supports create mode */
  supportsCreateMode?: boolean;
}

/**
 * Base result type returned by entity modal hooks
 */
export interface BaseEntityModalResult<TEntity, TId> {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** The loaded entity data (null when loading or in create mode) */
  entity: TEntity | null;
  /** Whether the entity is currently loading */
  isLoading: boolean;
  /** Open the modal for editing a specific entity */
  open: (id: TId) => void;
  /** Close the modal */
  close: () => void;
}

/**
 * Extended result type for modals that support create mode
 */
export interface CreateModeEntityModalResult<TEntity, TId>
  extends BaseEntityModalResult<TEntity, TId> {
  /** Whether the modal is in create mode (vs edit mode) */
  isCreateMode: boolean;
  /** Open the modal for creating a new entity */
  openCreate: () => void;
}

/**
 * Factory function that creates entity modal hooks with standardized behavior.
 *
 * This eliminates duplication across modal hooks by providing common:
 * - State management for selected entity ID
 * - Modal open/close logic with stale cache prevention
 * - Optional create mode support
 *
 * The modal only opens when the loaded entity ID matches the selected ID,
 * preventing stale cached data from being displayed.
 *
 * @example
 * // Basic usage without create mode
 * const useMyEntityModal = createEntityModalHook({
 *   useQueryHook: useMyEntity,
 *   getEntityId: (entity) => entity.id,
 * })
 *
 * @example
 * // With create mode support
 * const useMyEntityModal = createEntityModalHook({
 *   useQueryHook: useMyEntity,
 *   getEntityId: (entity) => entity.id,
 *   supportsCreateMode: true,
 * })
 */
export function createEntityModalHook<TEntity, TId = number>(
  config: EntityModalHookConfig<TEntity, TId> & { supportsCreateMode: true }
): () => CreateModeEntityModalResult<TEntity, TId>;

export function createEntityModalHook<TEntity, TId = number>(
  config: EntityModalHookConfig<TEntity, TId> & { supportsCreateMode?: false }
): () => BaseEntityModalResult<TEntity, TId>;

export function createEntityModalHook<TEntity, TId = number>(
  config: EntityModalHookConfig<TEntity, TId>
): () =>
  | BaseEntityModalResult<TEntity, TId>
  | CreateModeEntityModalResult<TEntity, TId> {
  const { useQueryHook, getEntityId, supportsCreateMode = false } = config;

  return function useEntityModal() {
    const [selectedId, setSelectedId] = useState<TId | null>(null);
    const [isCreateMode, setIsCreateMode] = useState(false);

    const { data: entity, isLoading } = useQueryHook(selectedId);

    // Modal is open when:
    // 1. In create mode (if supported), OR
    // 2. The loaded entity matches selectedId (prevents stale cached data)
    const isEntityReady = entity != null && getEntityId(entity) === selectedId;
    const isOpen = (supportsCreateMode && isCreateMode) || isEntityReady;

    const open = useCallback((id: TId) => {
      if (supportsCreateMode) {
        setIsCreateMode(false);
      }
      setSelectedId(id);
    }, []);

    const close = useCallback(() => {
      setSelectedId(null);
      if (supportsCreateMode) {
        setIsCreateMode(false);
      }
    }, []);

    const openCreate = useCallback(() => {
      setSelectedId(null);
      setIsCreateMode(true);
    }, []);

    const baseResult: BaseEntityModalResult<TEntity, TId> = useMemo(
      () => ({
        isOpen,
        entity: entity ?? null,
        isLoading,
        open,
        close,
      }),
      [isOpen, entity, isLoading, open, close]
    );

    if (supportsCreateMode) {
      return {
        ...baseResult,
        isCreateMode,
        openCreate,
      } as CreateModeEntityModalResult<TEntity, TId>;
    }

    return baseResult;
  };
}
